import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { auth, db } from "./firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  updateDoc,
  onSnapshot,
} from "firebase/firestore";
import { signOutUser, updateUserProfile, updateUserPassword, updateUserEmail } from './utils/authHelpers';
// Firebase Storage rimosso - ora usiamo Imgur per gli avatar
import { HiOutlineCog6Tooth } from "react-icons/hi2";
import Navbar from "./Navbar";
import BottomNavBar from "./BottomNavBar";
import { getInitials, moderateImage } from "./utils";
import { moderateUsername } from "./utils/contentModeration";
import CollaboratorsModal from "./CollaboratorsModal";
import FollowersModal from "./FollowersModal";
import LazyImage from './LazyImage';
import SwipeWrapper from './SwipeWrapper';
import "./Profile.css";

export default function Profile() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/feed";
  const user = auth.currentUser;
  const { t, i18n } = useTranslation();
  const [wishlists, setWishlists] = useState([]);
  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [userAvatar, setUserAvatar] = useState("");

  const [language, setLanguage] = useState("it");
  
  // Check if user signed in with Google
  const isGoogleUser = user?.providerData?.some(provider => provider.providerId === 'google.com');

  const [showNameModal, setShowNameModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(null);
  const [showFollowersModal, setShowFollowersModal] = useState({ show: false, type: null });
  const [showPremiumCancelModal, setShowPremiumCancelModal] = useState(false);
  const [isPremium, setIsPremium] = useState(false);

  const [newName, setNewName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");

  const [showSuccess, setShowSuccess] = useState("");
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [cancellingPremium, setCancellingPremium] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const data = userSnap.data();
        setFollowerCount(data.followers?.length || 0);
        setFollowingCount(data.following?.length || 0);
        setLanguage(data.language || "it");
        setUserAvatar(data.avatar || "");
        setIsPremium(data.isPremium || false);
      }
      
      // Query per le wishlist dell'utente (proprietario)
      const ownWishlistsQuery = query(
        collection(db, "wishlists"), 
        where("userId", "==", user.uid)
      );
      
      // Query per le wishlist dove l'utente è collaboratore
      const collaboratorWishlistsQuery = query(
        collection(db, "wishlists"),
        where("collaborators", "array-contains", user.uid)
      );

      let allWishlists = {
        own: [],
        collaborator: []
      };

      const updateWishlists = () => {
        const newWishlists = [...allWishlists.own, ...allWishlists.collaborator];
        setWishlists(newWishlists);
        
        const savedScrollPosition = sessionStorage.getItem('fromScroll');
        const fromPage = sessionStorage.getItem('fromPage');
        const fromBottomNav = location.state?.fromBottomNav;
        const shouldRestore = !fromBottomNav && savedScrollPosition && fromPage === '/profile';
        
        if (shouldRestore && newWishlists.length > 0) {
          setTimeout(() => {
            const position = parseInt(savedScrollPosition, 10);
            if (!isNaN(position)) {
              window.scrollTo({
                top: position,
                behavior: 'smooth'
              });
              
              sessionStorage.removeItem('fromScroll');
              sessionStorage.removeItem('fromPage');
            }
          }, 800);
        }
      };

      const unsubscribeOwn = onSnapshot(ownWishlistsQuery, (snap) => {
        allWishlists.own = snap.docs.map((d) => ({ 
          id: d.id, 
          ...d.data(), 
          isOwner: true 
        }));
        updateWishlists();
      });

      const unsubscribeCollaborator = onSnapshot(collaboratorWishlistsQuery, (snap) => {
        allWishlists.collaborator = snap.docs.map((d) => ({ 
          id: d.id, 
          ...d.data(), 
          isOwner: false,
          isShared: true 
        }));
        updateWishlists();
      });

      // Cleanup function per unsub
      return () => {
        unsubscribeOwn();
        unsubscribeCollaborator();
      };
    };
    fetchData();
  }, [user]);

  const handleChangeName = async () => {
    if (!newName.trim()) {
      setShowSuccess(t('profile.nameRequired') || 'Il nome è obbligatorio');
      setTimeout(() => setShowSuccess(""), 3000);
      return;
    }

    if (newName.trim().length < 2) {
      setShowSuccess(t('profile.nameTooShort') || 'Il nome deve avere almeno 2 caratteri');
      setTimeout(() => setShowSuccess(""), 3000);
      return;
    }

    // 🛡️ Username moderation check
    try {
      console.log("🛡️ Moderazione nuovo nome utente:", newName.trim());
      const moderationResult = await moderateUsername(newName.trim());
      
      if (!moderationResult.allowed) {
        console.log("🚫 Nome utente bloccato:", moderationResult.reason);
        setShowSuccess(t('profile.inappropriateUsername') || "Il nome utente non è appropriato. Scegliere un nome diverso.");
        setTimeout(() => setShowSuccess(""), 5000);
        return;
      }
      
      console.log("✅ Nome utente approvato dalla moderazione");
    } catch (moderationError) {
      console.error("❌ Errore moderazione nome utente:", moderationError);
      setShowSuccess(t('profile.usernameCheckFailed') || "Impossibile verificare il nome utente. Riprova.");
      setTimeout(() => setShowSuccess(""), 5000);
      return;
    }

    try {
      console.log('🔄 Aggiornamento nome utente:', newName.trim());
      
      // Aggiorna il profilo Firebase Auth
      await updateUserProfile({ displayName: newName.trim() });
      
      // Aggiorna il documento Firestore
      await updateDoc(doc(db, "users", user.uid), { 
        displayName: newName.trim(),
        updatedAt: new Date()
      });

      console.log('✅ Nome utente aggiornato con successo');
      
      setShowNameModal(false);
      setNewName("");
      setShowSuccess(t('profile.nameUpdated'));
      setTimeout(() => setShowSuccess(""), 3000);
      
      // Forza un refresh per vedere il nuovo nome
      setTimeout(() => window.location.reload(), 1000);
      
    } catch (error) {
      console.error('❌ Errore aggiornamento nome:', error);
      
      let errorMessage = t('profile.updateError');
      if (error.code === 'auth/requires-recent-login') {
        errorMessage = t('auth.errors.requiresRecentLogin') || 'Devi effettuare di nuovo il login per questa operazione';
      }
      
      setShowSuccess(errorMessage);
      setTimeout(() => setShowSuccess(""), 5000);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword) {
      setShowSuccess(t('profile.passwordRequired') || 'La password è obbligatoria');
      setTimeout(() => setShowSuccess(""), 3000);
      return;
    }

    if (newPassword.length < 6) {
      setShowSuccess(t('auth.errors.weakPassword') || 'La password deve avere almeno 6 caratteri');
      setTimeout(() => setShowSuccess(""), 3000);
      return;
    }

    try {
      console.log('🔄 Aggiornamento password utente');
      
      await updateUserPassword(newPassword);
      
      console.log('✅ Password aggiornata con successo');
      
      setShowPasswordModal(false);
      setNewPassword("");
      setShowSuccess(t('profile.passwordUpdated'));
      setTimeout(() => setShowSuccess(""), 3000);
      
    } catch (error) {
      console.error('❌ Errore aggiornamento password:', error);
      
      let errorMessage = t('profile.updateError');
      
      switch (error.code) {
        case 'auth/requires-recent-login':
          errorMessage = t('auth.errors.requiresRecentLogin') || 'Devi effettuare di nuovo il login per questa operazione';
          break;
        case 'auth/weak-password':
          errorMessage = t('auth.errors.weakPassword') || 'La password è troppo debole';
          break;
        case 'auth/invalid-password':
          errorMessage = t('auth.errors.invalidPassword') || 'Password non valida';
          break;
      }
      
      setShowSuccess(errorMessage);
      setTimeout(() => setShowSuccess(""), 5000);
    }
  };

  const handleChangeEmail = async () => {
    if (!newEmail.trim()) {
      setShowSuccess(t('profile.emailRequired') || 'L\'email è obbligatoria');
      setTimeout(() => setShowSuccess(""), 3000);
      return;
    }

    // Validazione email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      console.log('❌ Email non valida:', newEmail.trim());
      setShowSuccess(t('auth.errors.invalidEmail') || 'Email non valida');
      setTimeout(() => setShowSuccess(""), 3000);
      return;
    }

    if (newEmail.trim() === user?.email) {
      console.log('❌ Email identica a quella attuale');
      setShowSuccess(t('profile.sameEmail') || 'Questa è già la tua email attuale');
      setTimeout(() => setShowSuccess(""), 3000);
      return;
    }

    try {
      console.log('🔄 Tentativo aggiornamento email:');
      console.log('- Email attuale:', user?.email);
      console.log('- Nuova email:', newEmail.trim());
      console.log('- User UID:', user?.uid);
      
      // Aggiorna l'email in Firebase Auth
      await updateUserEmail(newEmail.trim());
      console.log('✅ Email aggiornata in Firebase Auth');
      
      // Aggiorna il documento Firestore
      await updateDoc(doc(db, "users", user.uid), { 
        email: newEmail.trim(),
        updatedAt: new Date()
      });
      console.log('✅ Email aggiornata in Firestore');

      console.log('✅ Aggiornamento email completato con successo');
      
      setShowEmailModal(false);
      setNewEmail("");
      setShowSuccess(t('profile.emailUpdated') || 'Email aggiornata con successo!');
      setTimeout(() => setShowSuccess(""), 3000);
      
      // Forza un refresh per vedere la nuova email
      setTimeout(() => window.location.reload(), 1000);
      
    } catch (error) {
      console.error('❌ Errore completo aggiornamento email:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      let errorMessage = t('profile.updateError') || 'Errore durante l\'aggiornamento. Riprova.';
      
      switch (error.code) {
        case 'auth/requires-recent-login':
          errorMessage = t('auth.errors.requiresRecentLogin') || 'Devi effettuare di nuovo il login per questa operazione. Disconnettiti e riaccedi.';
          console.log('🔒 Richiesta autenticazione recente');
          break;
        case 'auth/email-already-in-use':
          errorMessage = t('auth.errors.emailInUse') || 'Questa email è già utilizzata da un altro account';
          console.log('📧 Email già in uso nel sistema');
          break;
        case 'auth/invalid-email':
          errorMessage = t('auth.errors.invalidEmail') || 'Formato email non valido';
          console.log('❌ Formato email non valido');
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'Operazione non consentita. Contatta il supporto.';
          console.log('🚫 Operazione non consentita');
          break;
        case 'auth/user-disabled':
          errorMessage = 'Account disabilitato. Contatta il supporto.';
          console.log('🔒 Account disabilitato');
          break;
        default:
          console.log('❓ Errore sconosciuto:', error.code);
          break;
      }
      
      setShowSuccess(errorMessage);
      setTimeout(() => setShowSuccess(""), 6000);
    }
  };

  const handleChangeLanguage = async (lng) => {
    try {
      await updateDoc(doc(db, "users", user.uid), { language: lng });
      setLanguage(lng);
      i18n.changeLanguage(lng);
      setShowLanguageModal(false);

      // Force a small delay to ensure the language change is applied
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error("Language update error:", error);
      alert(t('profile.updateError'));
    }
  };

  const handleCancelPremium = async () => {
    if (!user) return;
    
    setCancellingPremium(true);
    try {
      // Update user's premium status to false
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        isPremium: false,
        premiumCancelledAt: new Date(),
        updatedAt: new Date()
      });
      
      // Update local state
      setIsPremium(false);
      setShowPremiumCancelModal(false);
      
      // Update localStorage
      localStorage.setItem('userPremiumStatus', 'false');
      
      // Show success message
      setShowSuccess(t('common.success') || 'Premium cancelled successfully');
      setTimeout(() => setShowSuccess(""), 3000);
      
      console.log('✅ Premium subscription cancelled successfully');
    } catch (error) {
      console.error('❌ Error cancelling premium:', error);
      setShowSuccess(t('errors.general') || 'Error occurred. Please try again.');
      setTimeout(() => setShowSuccess(""), 3000);
    } finally {
      setCancellingPremium(false);
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log("🔄 Inizio upload avatar:", file.name, file.size);

    // Check if user is authenticated
    if (!user || !auth.currentUser) {
      console.error("❌ Utente non autenticato");
      setShowSuccess('Devi essere autenticato per caricare un avatar');
      setTimeout(() => setShowSuccess(""), 3000);
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setShowSuccess(t('profile.avatarTooLarge'));
      setTimeout(() => setShowSuccess(""), 3000);
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      setShowSuccess(t('profile.invalidFileType'));
      setTimeout(() => setShowSuccess(""), 3000);
      return;
    }

    setIsUploadingAvatar(true);

    try {
      console.log("🔍 Moderazione immagine...");

      // Moderate image before upload
      const isSafe = await moderateImage(file);
      if (!isSafe) {
        setShowSuccess(t('profile.inappropriateImage'));
        setTimeout(() => setShowSuccess(""), 5000);
        return;
      }

      console.log("✅ Immagine approvata dalla moderazione");
      console.log("⬆️ Upload avatar con Imgur...");

      // Convert file to base64
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result.split(',')[1]; // Remove data:image/jpeg;base64,
          resolve(result);
        };
        reader.readAsDataURL(file);
      });

      // Upload to Imgur
      const response = await fetch('https://api.imgur.com/3/image', {
        method: 'POST',
        headers: {
          'Authorization': 'Client-ID 546c25a59c58ad7', // Free Imgur Client ID
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image: base64,
          type: 'base64'
        })
      });

      if (!response.ok) {
        throw new Error(`Imgur API error: ${response.status}`);
      }

      const result = await response.json();
      const imageUrl = result.data.link;

      console.log("✅ Avatar caricato su Imgur:", imageUrl);

      // Update user profile and database
      await updateUserProfile({ photoURL: imageUrl });
      await updateDoc(doc(db, "users", user.uid), { avatar: imageUrl });

      setUserAvatar(imageUrl);
      setShowAvatarModal(false);
      setShowSuccess(t('profile.avatarUpdated'));
      setTimeout(() => setShowSuccess(""), 3000);

    } catch (error) {
      console.error("❌ Errore upload avatar:", error);

      let errorMessage = t('profile.imageUploadError');
      if (error.message.includes('429')) {
        errorMessage = t('profile.networkErrorUpload');
      } else if (error.message.includes('Network')) {
        errorMessage = t('profile.networkErrorUpload');
      }

      setShowSuccess(errorMessage);
      setTimeout(() => setShowSuccess(""), 5000);
    } finally {
      console.log("🏁 Upload terminato");
      setIsUploadingAvatar(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      // Update user profile (no need to delete from Imgur, it stays there)
      await updateUserProfile({ photoURL: null });
      await updateDoc(doc(db, "users", user.uid), { avatar: "" });

      setUserAvatar("");
      setShowAvatarModal(false);
      setShowSuccess(t('profile.avatarRemoved'));
      setTimeout(() => setShowSuccess(""), 3000);
    } catch (error) {
      console.error("Avatar removal error:", error);
      alert(t('profile.updateError'));
    }
  };

  const handleLogout = async () => {
    await signOutUser();
    navigate("/login");
  };

  const handleDeleteAccount = async () => {
    await deleteDoc(doc(db, "users", user.uid));
    await signOutUser();
    navigate("/login");
  };

  return (
    <SwipeWrapper redirectTo={from}>
      <Navbar />
      <div style={styles.page}>
        <div style={styles.header}>
          <div 
            className="avatar-container"
            style={styles.avatarContainer}
            onClick={() => setShowAvatarModal(true)}
          >
            {userAvatar ? (
              <img src={userAvatar} alt="Avatar" style={styles.avatarImage} />
            ) : (
              <div style={styles.avatar}>{getInitials(user?.displayName)}</div>
            )}
            <div className="avatar-overlay">
              <span className="avatar-text">{t('profile.changeAvatar')}</span>
            </div>
          </div>
          <HiOutlineCog6Tooth
            style={styles.settingsIcon}
            onClick={() => setShowSettingsModal(true)}
          />
          <h2 style={styles.name}>{user?.displayName}</h2>
          <p style={styles.email}>{user?.email}</p>
          <div style={styles.stats}>
            <span 
              style={styles.clickableStat}
              onClick={() => setShowFollowersModal({ show: true, type: 'followers' })}
            >
              <strong>{followerCount}</strong> {t('profile.followers')}
            </span>
            <span 
              style={styles.clickableStat}
              onClick={() => setShowFollowersModal({ show: true, type: 'following' })}
            >
              <strong>{followingCount}</strong> {t('profile.following')}
            </span>
          </div>
        </div>

        <div style={styles.settings}>
          <button style={styles.btn} onClick={() => setShowNameModal(true)}>{t('profile.editName')}</button>
          {!isGoogleUser && (
            <>
              <button style={styles.btn} onClick={() => setShowPasswordModal(true)}>{t('profile.changePassword')}</button>
              <button style={styles.btn} onClick={() => setShowEmailModal(true)}>{t('profile.changeEmail')}</button>
            </>
          )}
          {isPremium && (
            <button 
              style={{...styles.btn, backgroundColor: '#000000'}} 
              onClick={() => setShowPremiumCancelModal(true)}
            >
              {t('premium.cancel.cancelSubscription')}
            </button>
          )}
        </div>

        <h3 style={styles.sectionTitle}>{t('navigation.myWishlists')}</h3>
        <div style={styles.grid}>
          {wishlists.map(w => (
            <div key={w.id} style={styles.card} onClick={() => {
              // Salva la posizione di scroll corrente prima di navigare
              sessionStorage.setItem('fromScroll', window.scrollY.toString());
              sessionStorage.setItem('fromPage', '/profile');
              navigate(`/wishlist/${w.id}`, { state: { from: "/profile" } });
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <h4 style={styles.cardTitle}>{w.name}</h4>
                {!w.isOwner && (
                  <span style={{
                    fontSize: "12px",
                    background: "#e3f2fd",
                    color: "#1565c0",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontWeight: "500"
                  }}>
                    {t('wishlist.collaborator')}
                  </span>
                )}
                {(w.isShared || (w.collaborators && w.collaborators.length > 0)) && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowCollaboratorsModal(w.id);
                    }}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "20px",
                      height: "20px",
                      border: "2px solid #000",
                      borderRadius: "50%",
                      backgroundColor: "transparent"
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                  </div>
                )}
              </div>
              <p style={styles.cardSub}>{w.isPublic ? t('wishlist.public') : t('wishlist.private')}</p>
              <div style={styles.cardProducts}>
                {(w.products || []).slice(0, 3).map((p, i) => (
                  <LazyImage 
                    key={i} 
                    src={p.image} 
                    alt={p.title} 
                    style={styles.productImg} 
                  />
                ))}
                {(w.products?.length || 0) > 3 && (
                  <div style={styles.moreOverlay}>+{w.products.length - 3}</div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div style={styles.privacyTerms}>
          <span style={styles.privacyLink} onClick={() => navigate("/privacy")}>{t('legal.privacyPolicy')}</span>
          <span style={styles.privacyLink} onClick={() => navigate("/terms")}>{t('legal.termsAndConditions')}</span>
        </div>
      </div>

      <BottomNavBar />

      {/* Modali */}
      {showNameModal && (
        <Modal
          title={t('profile.confirmNameChange')}
          placeholder={t('profile.newName')}
          value={newName}
          onChange={setNewName}
          onConfirm={handleChangeName}
          onCancel={() => setShowNameModal(false)}
        />
      )}
      {showPasswordModal && (
        <Modal
          title={t('profile.confirmPasswordChange')}
          placeholder={t('profile.newPassword')}
          type="password"
          value={newPassword}
          onChange={setNewPassword}
          onConfirm={handleChangePassword}
          onCancel={() => setShowPasswordModal(false)}
        />
      )}
      {showEmailModal && (
        <Modal
          title={t('profile.confirmEmailChange')}
          placeholder={t('profile.newEmail')}
          type="email"
          value={newEmail}
          onChange={setNewEmail}
          onConfirm={handleChangeEmail}
          onCancel={() => setShowEmailModal(false)}
        />
      )}
      
      {showPremiumCancelModal && (
        <div style={styles.overlay} onClick={() => setShowPremiumCancelModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: 20 }}>{t('premium.cancel.cancelConfirmTitle')}</h3>
            <p style={{ marginBottom: 20, color: '#666' }}>
              {t('premium.cancel.cancelConfirmMessage')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
              <button 
                style={{...styles.modalBtn, backgroundColor: '#000000', width: '100%'}} 
                onClick={handleCancelPremium}
                disabled={cancellingPremium}
              >
                {cancellingPremium ? t('common.loading') : t('premium.cancel.confirmCancel')}
              </button>
              <button 
                style={{...styles.cancelBtn, width: '100%'}} 
                onClick={() => setShowPremiumCancelModal(false)}
                disabled={cancellingPremium}
              >
                {t('premium.cancel.keepPremium')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showSettingsModal && (
        <div style={styles.overlay} onClick={() => setShowSettingsModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button style={styles.modalBtn} onClick={() => setShowLogoutModal(true)}>{t('auth.logout')}</button>
            <button style={styles.modalBtn} onClick={() => setShowDeleteModal(true)}>{t('profile.deleteAccount')}</button>
            <button style={styles.modalBtn} onClick={() => setShowLanguageModal(true)}>{t('profile.changeLanguage')} ({language})</button>
            <button style={styles.cancelBtn} onClick={() => setShowSettingsModal(false)}>{t('common.cancel')}</button>
          </div>
        </div>
      )}
      {showLanguageModal && (
        <div style={styles.overlay} onClick={() => setShowLanguageModal(false)}>
          <div style={styles.languageModal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.title}>{t('profile.selectLanguage')}</h3>
            <div style={styles.scrollArea}>
              {[
                { code: "it", name: "Italiano" },
                { code: "en", name: "English" },
                { code: "es", name: "Español" },
                { code: "de", name: "Deutsch" },
                { code: "fr", name: "Français" },

              ].map((lng) => (
                <div
                  key={lng.code}
                  style={styles.languageOption}
                  onClick={() => handleChangeLanguage(lng.code)}
                >
                  {lng.name}
                </div>
              ))}
            </div>
            <button style={styles.cancelBtn} onClick={() => setShowLanguageModal(false)}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
      {showLogoutModal && (
        <ConfirmModal
          title={t('profile.confirmLogout')}
          onConfirm={handleLogout}
          onCancel={() => setShowLogoutModal(false)}
        />
      )}
      {showDeleteModal && (
        <ConfirmModal
          title={t('profile.confirmDeleteAccount')}
          onConfirm={handleDeleteAccount}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
      {showAvatarModal && (
        <div style={styles.overlay} onClick={() => setShowAvatarModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: 20 }}>{t('profile.changeAvatar')}</h3>
            <input
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              style={{ display: 'none' }}
              ref={fileInputRef}
            />
            <button 
              style={styles.modalBtn} 
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploadingAvatar}
            >
              {isUploadingAvatar ? t('profile.uploading') : t('profile.selectPhoto')}
            </button>
            {userAvatar && (
              <button style={styles.modalBtn} onClick={handleRemoveAvatar}>
                {t('profile.removeAvatar')}
              </button>
            )}
            <button style={styles.cancelBtn} onClick={() => setShowAvatarModal(false)}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {showSuccess && (
        <div style={styles.toast}>{showSuccess}</div>
      )}

      {showCollaboratorsModal && (
        <CollaboratorsModal
          wishlistId={showCollaboratorsModal}
          currentUserId={auth.currentUser?.uid}
          onClose={() => setShowCollaboratorsModal(null)}
          viewOnly={false}
        />
      )}

      {showFollowersModal.show && (
        <FollowersModal
          isOpen={showFollowersModal.show}
          onClose={() => setShowFollowersModal({ show: false, type: null })}
          userId={user?.uid}
          type={showFollowersModal.type}
          userDisplayName={user?.displayName || 'Utente'}
        />
      )}
    </SwipeWrapper>
  );
}

function Modal({ title, placeholder, type = "text", value, onChange, onConfirm, onCancel }) {
  const { t } = useTranslation();
  return (
    <div style={styles.overlay} onClick={onCancel}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 20 }}>{title}</h3>
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={styles.input}
        />
        <button style={styles.modalBtn} onClick={onConfirm}>{t('common.confirm')}</button>
        <button style={styles.cancelBtn} onClick={onCancel}>{t('common.cancel')}</button>
      </div>
    </div>
  );
}

function ConfirmModal({ title, onConfirm, onCancel }) {
  const { t } = useTranslation();
  return (
    <div style={styles.overlay} onClick={onCancel}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 20 }}>{title}</h3>
        <button style={styles.modalBtn} onClick={onConfirm}>{t('common.confirm')}</button>
        <button style={styles.cancelBtn} onClick={onCancel}>{t('common.cancel')}</button>
      </div>
    </div>
  );
}

const styles = {
  toast: {
    position: "fixed",
    top: "80px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#111",
    color: "#fff",
    padding: "12px 20px",
    borderRadius: "8px",
    zIndex: 9999,
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    fontSize: "14px",
    fontFamily: "Poppins, sans-serif",
  },

  page: {
    background: "#fff",
    minHeight: "100vh",
    padding: "20px",
    fontFamily: "Poppins, sans-serif",
    paddingTop: "80px",
    paddingBottom: "80px",
  },
  header: {
    textAlign: "center",
    marginBottom: "30px",
    position: "relative",
  },
  avatarContainer: {
    position: "relative",
    width: "100px",
    height: "100px",
    margin: "0 auto 10px",
    cursor: "pointer",
  },
  avatar: {
    width: "100px",
    height: "100px",
    borderRadius: "50%",
    backgroundColor: "#000", // SEMPRE nero
    color: "#fff", // SEMPRE bianco
    fontSize: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "600",
    boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
    border: "3px solid #fff",
  },
  avatarImage: {
    width: "100px",
    height: "100px",
    borderRadius: "50%",
    objectFit: "cover",
  },
  avatarOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  avatarText: {
    color: "#fff",
    fontSize: "12px",
    fontWeight: "500",
    textAlign: "center",
  },
  settingsIcon: {
    position: "absolute",
    top: "-60px",  // puoi regolarlo -50 se vuoi più vicino
    right: "10px",
    fontSize: "26px",
    cursor: "pointer",
  },
  name: {
    margin: "6px 0 2px",
    fontSize: "20px",
    fontWeight: "600",
    color: "#111",
  },
  email: {
    fontSize: "13px",
    color: "#666",
  },
  stats: {
    display: "flex",
    justifyContent: "center",
    gap: "20px",
    marginTop: "10px",
    fontSize: "13px",
    color: "#333",
  },
  clickableStat: {
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: "8px",
    transition: "background-color 0.2s ease",
  },
  settings: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    marginBottom: "30px",
  },
  btn: {
    background: "#000",
    color: "#fff",
    padding: "12px",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "600",
    marginBottom: "14px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "16px",
  },
  card: {
    background: "#f9f9f9",
    borderRadius: "16px",
    padding: "16px",
    border: "1px solid #eee",
    cursor: "pointer",
  },
  cardTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: "600",
    color: "#000",
    flex: 1
  },
  cardSub: {
    fontSize: "13px",
    color: "#666",
    marginBottom: "8px",
  },
  cardProducts: {
    display: "flex",
    gap: "6px",
  },
  productImg: {
    width: "60px",
    height: "60px",
    borderRadius: "10px",
    objectFit: "cover",
  },
  moreOverlay: {
    width: "60px",
    height: "60px",
    borderRadius: "10px",
    backgroundColor: "#000",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    fontWeight: "600",
  },
  privacyTerms: {
    marginTop: "50px",
    fontSize: "12px",
    color: "#666",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    gap: "20px",
    borderTop: "1px solid #eee",
    paddingTop: "16px",
    paddingBottom: "20px",
  },
  privacyLink: {
    cursor: "pointer",
    textDecoration: "underline",
  },
  overlay: {
    position: "fixed",
    top: 0, left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  modal: {
    backgroundColor: "#fff",
    padding: "24px",
    borderRadius: "16px",
    width: "90%",
    maxWidth: "320px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
    textAlign: "center",
    fontFamily: "Poppins, sans-serif",
    boxSizing: "border-box",
  },
  input: {
    width: "100%",
    padding: "12px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    marginBottom: "16px",
    fontSize: "14px",
    boxSizing: "border-box",
  },
  modalBtn: {
    background: "#000",
    color: "#fff",
    padding: "12px",
    border: "none",
    borderRadius: "8px",
    width: "100%",
    cursor: "pointer",
    marginBottom: "10px",
    fontSize: "14px",
  },
  cancelBtn: {
    background: "#aaa",
    color: "#fff",
    padding: "12px",
    border: "none",
    borderRadius: "8px",
    width: "100%",
    cursor: "pointer",
    fontSize: "14px",
  },
  languageModal: {
    backgroundColor: "#f9f9f9",
    padding: "24px",
    borderRadius: "16px",
    width: "90%",
    maxWidth: "380px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    textAlign: "center",
    fontFamily: "Poppins, sans-serif",
    display: "flex",
    flexDirection: "column",
  },
  title: {
    fontSize: "18px",
    fontWeight: "600",
    marginBottom: "16px",
    color: "#111",
  },
  scrollArea: {
    maxHeight: "300px",
    overflowY: "auto",
    marginBottom: "16px",
  },
  languageOption: {
    padding: "12px",
    borderRadius: "8px",
    backgroundColor: "#fff",
    marginBottom: "8px",
    border: "1px solid #ddd",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
};